import clsx from "clsx";

/**
 * Props interface for the CheckboxField component.
 */
interface CheckboxFieldProps {
  /** The value of the checkbox */
  value: string;
  /** The label text to display */
  label: string;
  /** Error message to display */
  error?: string;
  /** Additional CSS classes */
  className?: string;
  /** React Hook Form register props */
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.Ref<HTMLInputElement>;
  /** Current form value array to determine checked state */
  formValue?: string[];
}

/**
 * Checkbox field component for form inputs.
 *
 * Displays a checkbox with label text, styled to match the container
 * design with the checkbox positioned on the right side. Designed to
 * work seamlessly with React Hook Form's register method for multiple
 * selections. Uses a square checkbox instead of a circular radio button.
 *
 * @example
 * ```tsx
 * <CheckboxField
 *   {...register("desired_gender")}
 *   value="female"
 *   label="Women"
 *   formValue={selectedValues}
 *   error={errors.desired_gender?.message}
 * />
 * ```
 */
export default function CheckboxField({
  value,
  label,
  error,
  className,
  name,
  onChange,
  onBlur,
  ref,
  formValue = [],
}: CheckboxFieldProps) {
  const isChecked = formValue.includes(value);

  return (
    <div className={clsx("relative", className)}>
      {/* Error Message */}
      {error && (
        <div className="absolute -top-6 left-0 text-red-600 text-sm font-medium" data-testid="field-error">
          {error}
        </div>
      )}

      {/* Checkbox Container — static test id + data-option-value (input is sr-only) */}
      {/* -m-4 p-4 extends label into Container's padding, making entire visible box clickable */}
      <label
        className="-m-4 p-4 flex items-center justify-between w-full cursor-pointer"
        data-testid="checkbox-field-option"
        data-option-value={value}
      >
        {/* Label Text */}
        <span className="min-w-0 flex-1 break-words pr-2 text-left text-lg font-medium text-black">{label}</span>

        {/* Checkbox */}
        <div className="relative">
          <input
            type="checkbox"
            name={name}
            value={value}
            onChange={onChange || (() => {})}
            onBlur={onBlur}
            ref={ref}
            checked={isChecked}
            className="sr-only" // Hide the default checkbox
          />

          {/* Custom Checkbox Square */}
          <div
            className={clsx(
              "w-6 h-6 border-2 bg-white rounded flex items-center justify-center transition-colors duration-200",
              {
                "border-pink-500": isChecked,
                "border-black": !isChecked,
              }
            )}
          >
            {/* Checkmark */}
            <div
              className={clsx("w-3 h-3 bg-pink-500 transition-opacity duration-200", {
                "opacity-100": isChecked,
                "opacity-0": !isChecked,
              })}
            >
              {/* Simple checkmark using CSS */}
              <div
                className={clsx("w-full h-full relative", {
                  "opacity-100": isChecked,
                  "opacity-0": !isChecked,
                })}
              />
            </div>
          </div>
        </div>
      </label>
    </div>
  );
}
